import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { env } from '../config/environment';
import './ProtectedRoute.css';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean; // New prop to require admin privileges
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, userEmail, isLoading, login } = useAuth();

  if (isLoading) {
    return (
      <div className="protected-route-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="protected-route-container">
        <div className="auth-required">
          <div className="auth-required-icon">🔒</div>
          <h2>Authentication Required</h2>
          <p>You must be signed in to access this page.</p>
          <button onClick={login} className="login-button">
            <span className="microsoft-icon">🔑</span>
            Sign in with Microsoft
          </button>
          <p className="auth-note">
            {requireAdmin 
              ? "Only authorized administrators can create and manage blog posts."
              : "Please sign in to continue."
            }
          </p>
        </div>
      </div>
    );
  }

  // Check for admin privileges if required
  if (requireAdmin && !isAdmin) {
    return (
      <div className="protected-route-container">
        <div className="auth-required">
          <div className="auth-required-icon">🚫</div>
          <h2>Admin Access Required</h2>
          <p>You are signed in as: <strong>{userEmail}</strong></p>
          <p>However, this page requires administrator privileges.</p>
          <div className="admin-info">
            <h3>Administrator Access</h3>
            <p>Only the following authorized administrators can access this page:</p>
            <ul>
              {env.authorizedAdmins.length > 0 ? (
                env.authorizedAdmins.map((email, index) => (
                  <li key={index}>{email}</li>
                ))
              ) : (
                <li>No administrators currently configured</li>
              )}
            </ul>
            <p>If you believe you should have access, please contact the site administrator.</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
